import { useEffect, useRef } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Keyboard behaviour for a modal panel. Attach the returned ref to the
 * panel element (together with role="dialog" aria-modal="true"):
 *   - Escape closes the dialog
 *   - focus moves into the panel on open and Tab / Shift+Tab stay inside it
 *   - focus returns to whatever opened the dialog when it closes
 */
export function useDialog<T extends HTMLElement = HTMLDivElement>(onClose: () => void, active = true) {
  const ref = useRef<T>(null);
  // Keep the latest onClose without re-running the effect (callers
  // usually pass an inline arrow).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // Remember the opener during render — by the time the effect runs an
  // autoFocus field inside the dialog has already taken focus.
  const openerRef = useRef<HTMLElement | null>(null);
  if (!active) openerRef.current = null;
  else if (openerRef.current === null && typeof document !== "undefined") {
    openerRef.current = document.activeElement as HTMLElement | null;
  }

  useEffect(() => {
    if (!active) return;
    const panel = ref.current;
    if (!panel) return;
    const opener = openerRef.current;

    // Respect an autoFocus inside the panel; otherwise focus the first control.
    if (!panel.contains(document.activeElement)) {
      const first = panel.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? panel).focus();
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null,
      );
      if (items.length === 0) { e.preventDefault(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (opener && opener.isConnected) opener.focus();
    };
  }, [active]);

  return ref;
}
